import React from 'react';
import { FileText, Shield, AlertTriangle, CreditCard, Ban, Scale } from 'lucide-react';

const Terms = () => {
  const sections = [
    {
      icon: FileText,
      title: "1. Acceptance of Terms",
      content: "By accessing or using the Twe Voice Assistant website or desktop application, you agree to be bound by these Terms of Service. If you do not agree, do not use the service."
    },
    {
      icon: Shield,
      title: "2. Description of Service",
      content: "Twe Voice Assistant is a desktop AI assistant that enables voice-based commands, AI-driven responses, automation and productivity workflows, and system-level integrations. Features may evolve over time."
    },
    {
      icon: AlertTriangle,
      title: "3. Eligibility",
      items: [
        "Be at least 13 years old",
        "Have legal capacity to enter into these terms",
        "Use the app in compliance with all applicable laws"
      ]
    },
    {
      icon: Ban,
      title: "4. User Responsibilities",
      content: "You agree not to:",
      items: [
        "Reverse engineer or exploit the software",
        "Abuse AI features or bypass usage limits",
        "Use the app for illegal, harmful, or deceptive purposes",
        "Attempt to access private APIs or backend systems"
      ]
    },
    {
      icon: AlertTriangle,
      title: "5. AI Disclaimer",
      content: "Twe uses AI models that may generate inaccurate or incomplete responses, or misinterpret voice commands. You acknowledge that AI output is not guaranteed to be correct, and you remain responsible for decisions made based on the output."
    },
    {
      icon: CreditCard,
      title: "6. Payments & Subscriptions",
      items: [
        "Some features may require a paid subscription",
        "Payments are processed by third-party providers",
        "All fees are non-refundable unless required by law"
      ]
    },
    {
      icon: Ban,
      title: "7. Termination",
      content: "We reserve the right to suspend or terminate access if these terms are violated, abuse or security risks are detected, or required by law."
    },
    {
      icon: AlertTriangle,
      title: "8. Limitation of Liability",
      content: "To the maximum extent permitted by law: Twe is provided 'as is'. We are not liable for indirect, incidental, or consequential damages. Our total liability is limited to the amount you paid in the last 12 months."
    },
    {
      icon: Scale,
      title: "9. Governing Law",
      content: "These Terms shall be governed by the laws applicable in your operating jurisdiction."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <FileText className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-400">
            Effective Date: January 1, 2025
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={index}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 hover:border-orange-500/30 transition-all duration-300"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">
                    {section.title}
                  </h2>
                </div>

                {section.content && (
                  <p className="text-gray-300 leading-relaxed mb-4">
                    {section.content}
                  </p>
                )}

                {section.items && (
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <span className="text-orange-500 mt-1">•</span>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Agreement Notice */}
        <div className="mt-12 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4">
            Agreement
          </h3>
          <p className="text-gray-300 leading-relaxed">
            By continuing to use Twe Voice Assistant, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </div>

        {/* Contact */}
        <div className="mt-8 text-center">
          <p className="text-gray-400">
            Questions? Contact us at{' '}
            <a
              href="mailto:contact@twe-assistant.com"
              className="text-orange-500 hover:text-orange-400 transition-colors duration-200"
            >
              contact@twe-assistant.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;